/**
 * Application header with user menu
 */

import { useAuth } from '@/core/auth/AuthContext'
import NotificationDropdown from '../NotificationDropdown'

interface HeaderProps {
  onMenuToggle: () => void
}

const Header = ({ onMenuToggle }: HeaderProps) => {
  const { user, logout, isLoggingOut } = useAuth()

  const handleLogout = async () => {
    if (isLoggingOut) return
    await logout()
  }

  return (
    <header className="fixed top-0 left-0 right-0 h-16 bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 shadow-lg z-20">
      <div className="flex items-center justify-between h-full px-4 md:px-6">
        <div className="flex items-center gap-3">
          {/* Hamburger menu button - mobile only */}
          <button
            onClick={onMenuToggle}
            className="md:hidden p-2 -ml-2 text-gray-300 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
            aria-label="Toggle menu"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          <div className="w-8 h-8 bg-red-600 rounded-lg flex items-center justify-center">
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>
          <h1 className="text-lg md:text-xl font-bold bg-gradient-to-r from-red-400 to-red-600 bg-clip-text text-transparent">
            <span className="hidden sm:inline">HR Management System</span>
            <span className="sm:hidden">HRMS</span>
          </h1>
        </div>

        <div className="flex items-center gap-2 md:gap-4">
          <NotificationDropdown />
          {/* User info - hidden on small mobile */}
          <div className="hidden sm:flex items-center gap-3 px-3 md:px-4 py-2 bg-gray-800 rounded-lg">
            <div className="w-8 h-8 bg-gradient-to-br from-red-500 to-red-700 rounded-full flex items-center justify-center">
              <span className="text-white text-sm font-semibold">
                {(user?.full_name || user?.display_name || 'U')
                  .split(' ')
                  .map(n => n[0])
                  .join('')
                  .toUpperCase()
                  .slice(0, 2)}
              </span>
            </div>
            <div className="hidden md:block text-right">
              <p className="text-sm font-medium text-white">
                {user?.full_name || user?.display_name}
              </p>
              <p className="text-xs text-gray-400">{user?.email}</p>
            </div>
          </div>
          {/* Mobile: just avatar */}
          <div className="sm:hidden w-8 h-8 bg-gradient-to-br from-red-500 to-red-700 rounded-full flex items-center justify-center">
            <span className="text-white text-sm font-semibold">
              {(user?.full_name || user?.display_name || 'U')
                .split(' ')
                .map(n => n[0])
                .join('')
                .toUpperCase()
                .slice(0, 2)}
            </span>
          </div>
          <button
            onClick={handleLogout}
            disabled={isLoggingOut}
            className="p-2 md:px-4 md:py-2 text-sm font-medium text-gray-300 hover:text-white hover:bg-gray-700 rounded-lg transition-all duration-200 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoggingOut ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-300"></div>
            ) : (
              <svg className="w-5 h-5 md:w-4 md:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
            )}
            <span className="hidden md:inline">
              {isLoggingOut ? 'Signing out...' : 'Logout'}
            </span>
          </button>
        </div>
      </div>
    </header>
  )
}

export default Header
